<template>
    <HeaderCardsLayout v-if="series" variant="No-Logo">
        <template #headline>{{ series.nameShort }}</template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="series.nameLong">
                <template #currentIcon>
                    <SingleEventIcon v-if="series.singleProject" class="inline-block align-sub shrink-0" />
                </template>
                <BreadcrumbLink :target="{ name: 'projects' }">
                    <ConstructionIcon class="mr-1 shrink-0" />
                    {{ $t('project-wiki') }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>

        <template v-if="series.singleProject" #icon><SingleEventIcon class="h-12 w-12" /></template>
        <template #headline-extra>
            <Badge
                v-if="getSeriesStatus(series.projects?.nodes.map((p) => p.status) ?? [])"
                :tooltip-text="$t('project-series-status-calculated')"
                size="Small"
                :variant="getSeriesBadgeVariant(series)"
            >
                {{ getSeriesBadgeLabel(series) }}
            </Badge>
            <div v-else class="text-gray-500 text-sm">({{ $t('no-projects') }})</div>
        </template>

        <template #buttons>
            <Button
                variant="Info-Light"
                size="Base"
                :disabled-explanation="convertDisabledExplanation"
                @click="toggleSingleProject"
            >
                <template v-if="!series.singleProject" #icon>
                    <SingleEventIcon class="size-5" />
                </template>
                {{ convertButtonLabel }}
            </Button>
            <Button
                variant="Danger-Strong"
                size="Base"
                :disabled-explanation="deleteDisabledExplanation"
                @click="deleteProjectSeries"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('delete') }}
            </Button>
            <LinkButton
                variant="Info-Strong"
                size="Base"
                :disabled-explanation="createProjectDisabledExplanation"
                :to="getCreateProjectLink(seriesId, series.nameLong)"
            >
                <template #icon>
                    <EditIcon class="size-5" />
                </template>
                {{ createProjectButtonLabel }}
            </LinkButton>
        </template>

        <template #tab-navigation>
            <TabNavigation
                :headline="$t('tab')"
                :items="tabNavigationItems"
                tab-panel-id="eventSeriesTabs"
                :tabs-disabled-text="tabsDisabledText"
                @focus-tab-panel="focusTabPanel"
            />
        </template>

        <template #body>
            <PaddedContent>
                <TabPanel :id="tabPanelId" ref="tabPanelRef" :headline="activeTabHeadline">
                    <RouterView />
                </TabPanel>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import Badge from '@/components/Badge/Badge.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import ConstructionIcon from '@/components/Icon/ConstructionIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import EditIcon from '@/components/Icon/EditIcon.vue';
import SingleEventIcon from '@/components/Icon/SingleEventIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import TabNavigation from '@/components/TabNavigation/TabNavigation.vue';
import TabPanel from '@/components/TabNavigation/TabPanel.vue';
import { useTabNavigation } from '@/components/TabNavigation/useTabNavigation';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { nanoid } from 'nanoid';
import { computed, provide, ref } from 'vue';
import { useProjectSeries } from '../composables/useProjectSeries';
import { projectSeriesKey } from '../keys';
import { getCreateProjectLink, getSeriesBadgeLabel, getSeriesBadgeVariant, getSeriesStatus } from '../project';

const fluent = useFluent();

const props = defineProps<{ seriesId: string }>();
const tabPanelRef = ref();
const tabPanelId = nanoid();

const seriesId = computed(() => props.seriesId);
const {
    series,
    loading,
    createProjectButtonLabel,
    convertButtonLabel,
    convertDisabledExplanation,
    deleteDisabledExplanation,
    createProjectDisabledExplanation,
    toggleSingleProject,
    deleteProjectSeries,
    updateProjectSeries,
    createProjectSeriesMultiLink,
} = useProjectSeries(seriesId);

provide(projectSeriesKey, {
    series,
    loading,
    createProjectButtonLabel,
    convertButtonLabel,
    convertDisabledExplanation,
    deleteDisabledExplanation,
    createProjectDisabledExplanation,
    toggleSingleProject,
    deleteProjectSeries,
    updateProjectSeries,
    createProjectSeriesMultiLink,
});

const projectSeriesTabs = [
    { route: 'project-series-overview-tab', label: fluent.$t('overview') },
    { route: 'project-series-details-tab', label: fluent.$t('master-data') },
    { route: 'project-series-org-tab', label: fluent.$t('organisational-structure') },
    { route: 'project-series-appointment-tab', label: fluent.$t('appointment-and-location') },
    { route: 'project-series-links-tab', label: fluent.$t('links') },
];

const { tabNavigationItems, activeTabHeadline, tabsDisabledText } = useTabNavigation(projectSeriesTabs);

function focusTabPanel() {
    tabPanelRef.value.focus();
}

useHeadSafe({ title: () => `${series.value?.nameShort} - ${fluent.$t('project-wiki')}` });
</script>
